import { useCallback } from "react";
import {
  resolveCreateBlock,
  useControlledAppAccess,
  useManagedAuthEnforcement,
  useQueryDomainFilterGuards,
  useQueryManagedGuards,
  type CreateBlockReason,
} from "@zapier/client";

export type { CreateBlockReason } from "@zapier/client";

/** Create rules for rendering: `getCreateBlock` returns why a create is blocked, or `undefined`. */
export function useConnectionCreateRules({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { isAppRestricted, isLoading: isControlledAppAccessLoading } =
    useControlledAppAccess({ enabled });
  const {
    isAppCreateRestricted,
    isLoading: isManagedGuardsLoading,
    isError: isManagedGuardsError,
  } = useQueryManagedGuards();
  const { isManagedAuthEnforced, isLoading: isManagedAuthEnforcementLoading } =
    useManagedAuthEnforcement();
  const { getDomainRestriction } = useQueryDomainFilterGuards();

  const getCreateBlock = useCallback(
    (
      appId: string,
      { isManaged = false }: { isManaged?: boolean } = {},
    ): CreateBlockReason | undefined =>
      resolveCreateBlock({
        isRestricted: isAppRestricted(appId),
        isManagedCreateRestricted:
          isManagedAuthEnforced && isAppCreateRestricted(appId),
        isManaged,
      }),
    [isAppRestricted, isAppCreateRestricted, isManagedAuthEnforced],
  );

  return {
    getCreateBlock,
    getDomainRestriction,
    isLoading:
      isControlledAppAccessLoading ||
      isManagedGuardsLoading ||
      isManagedAuthEnforcementLoading,
    isManagedGuardsError: isManagedAuthEnforced && isManagedGuardsError,
  };
}
